import { useMantineColorScheme } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useCallback, useEffect, useMemo, type PropsWithChildren } from "react";

import UserPreferencesContext, {
    ApplicationLayoutMode,
    ApplicationTheme,
    defaultUserPreferences,
    type IUserPreferences
} from "src/contexts/preferences/UserPreferencesContext";
import useCurrentUser from "src/hooks/authentication/useCurrentUser";

/**
 * Builds the per-user local-storage key using a trimmed, lowercase username.
 *
 * Postconditions: Falsy usernames use anonymous; whitespace-only usernames produce an empty suffix.
 */
export const getUserPreferencesStorageKey = (username?: string) =>
    `tmd-user-preferences:${(username || "anonymous").trim().toLowerCase()}`;

/**
 * Selects the stored theme for modern layout and forces light for legacy layout.
 */
export const getAppliedTheme = (preferences: IUserPreferences) =>
    preferences.layout === ApplicationLayoutMode.Modern
        ? preferences.theme
        : ApplicationTheme.Light;

/**
 * Accepts objects containing supported layout and theme values; extra properties are retained.
 */
const isUserPreferences = (value: unknown): value is IUserPreferences => {
    if (!value || typeof value !== "object") {
        return false;
    }

    const preferences = value as Partial<IUserPreferences>;
    return (
        (preferences.layout === "legacy" || preferences.layout === "modern") &&
        (preferences.theme === "light" || preferences.theme === "dark")
    );
};

/**
 * Reads stored preferences, falling back to shared defaults for absent, invalid or malformed JSON.
 */
const deserializePreferences = (value?: string): IUserPreferences => {
    if (!value) {
        return defaultUserPreferences;
    }

    try {
        const parsed = JSON.parse(value) as unknown;
        return isUserPreferences(parsed) ? parsed : defaultUserPreferences;
    } catch {
        return defaultUserPreferences;
    }
};

/**
 * Persists per-user preferences and applies their effective Mantine color scheme.
 *
 * Preconditions: Render beneath the user and Mantine providers.
 * Postconditions: Descendants receive the current preferences and update actions. Storage and theme
 * changes are requested through the corresponding hooks; consumers observe updates on later renders.
 */
const UserPreferencesProvider = ({ children }: PropsWithChildren) => {
    const user = useCurrentUser();
    const { setColorScheme } = useMantineColorScheme();
    const [preferences, setStoredPreferences] = useLocalStorage<IUserPreferences>({
        key: getUserPreferencesStorageKey(user?.username),
        defaultValue: defaultUserPreferences,
        deserialize: deserializePreferences,
        getInitialValueInEffect: false
    });

    /**
     * Requests storage and color-scheme updates for the supplied preferences.
     *
     * Assumptions: The caller supplies valid preferences; this action does not validate them.
     */
    const setPreferences = useCallback(
        (nextPreferences: IUserPreferences) => {
            setStoredPreferences(nextPreferences);
            setColorScheme(getAppliedTheme(nextPreferences));
        },
        [setColorScheme, setStoredPreferences]
    );

    /**
     * Requests the opposite stored theme while retaining layout; legacy still displays light.
     */
    const toggleTheme = useCallback(
        () => {
            setPreferences({
                ...preferences,
                theme:
                    preferences.theme === ApplicationTheme.Dark
                        ? ApplicationTheme.Light
                        : ApplicationTheme.Dark
            });
        },
        [preferences, setPreferences]
    );

    useEffect(
        () => {
            setColorScheme(getAppliedTheme(preferences));
        },
        [preferences, setColorScheme]
    );

    const context = useMemo(
        () => ({ preferences, setPreferences, toggleTheme }),
        [preferences, setPreferences, toggleTheme]
    );

    return (
        <UserPreferencesContext.Provider value={context}>
            {children}
        </UserPreferencesContext.Provider>
    );
};

export default UserPreferencesProvider;
